import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Job {
  id: string;
  job_number: string | null;
  status: string;
  property_address: string;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  property_type: string | null;
  owner_name: string | null;
  owner_phone: string | null;
  owner_email: string | null;
  estimated_value: number | null;
  created_at: string | null;
  updated_at: string | null;
  insurance_company: string | null;
  claim_number: string | null;
  notes: string | null;
  org_id: string;
  assigned_to: string | null;
  workflow_type: string | null;
  [key: string]: any;
}

/**
 * Centralized job data fetching hook
 * Replaces 244 lines of duplicated logic in JobDetail.tsx
 */
export const useJobData = (jobId: string) => {
  const [job, setJob] = useState<Job | null>(null);
  const [photos, setPhotos] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [estimates, setEstimates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadJobData = useCallback(async () => {
    if (!jobId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch job details
      const { data: jobData, error: jobError } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', jobId)
        .single();

      if (jobError) throw jobError;
      setJob(jobData as any);

      // Fetch related data in parallel
      const [photosRes, docsRes, tasksRes, timelineRes, estimatesRes] = await Promise.all([
        supabase.from('photos').select('*').eq('job_id', jobId).order('created_at', { ascending: false }),
        supabase.from('documents').select('*').eq('job_id', jobId).order('created_at', { ascending: false }),
        supabase.from('tasks').select('*').eq('job_id', jobId).order('due_date', { ascending: true }),
        supabase.from('job_timeline').select('*').eq('job_id', jobId).order('created_at', { ascending: false }),
        supabase.from('job_estimates').select('*').eq('job_id', jobId).order('created_at', { ascending: false }),
      ]);

      setPhotos(photosRes.data || []);
      setDocuments(docsRes.data || []);
      setTasks(tasksRes.data || []);
      setEstimates(estimatesRes.data || []);

      // Enrich timeline with actor display names
      const tlRows = timelineRes.data || [];
      const userIds = Array.from(new Set(tlRows.map((r: any) => r.created_by).filter(Boolean)));
      let nameMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: profs } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', userIds as string[]);
        (profs || []).forEach((p: any) => {
          nameMap[p.id] = p.full_name || p.email || 'User';
        });
      }
      setTimeline(tlRows.map((r: any) => ({
        ...r,
        actor_name: r.created_by ? (nameMap[r.created_by] || 'User') : 'System',
      })));
    } catch (err: any) {
      console.error("Error loading job data:", err);
      setError(err.message);
      toast.error("Failed to load job data");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadJobData();
  }, [loadJobData]);

  return {
    job,
    photos,
    documents,
    tasks,
    timeline,
    estimates,
    loading,
    error,
    refetch: loadJobData,
  };
};
